import React, { useState } from 'react';
import { Icons } from './Icons';
import { addApp } from '../services/api';

interface AddAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAppAdded: () => void;
}

export function AddAppModal({ isOpen, onClose, onAppAdded }: AddAppModalProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await addApp({ name });
      onAppAdded();
      onClose();
      // Reset form
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add app');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-xl shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-2">
            <Icons.Plus className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Add Azure Web App</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg hover:text-red-400">
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-1.5">App Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-web-app"
              className="input-modern"
              required
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              The Azure Web App name (must match the name in Azure Monitor logs)
            </p>
          </div>

          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <div className="flex items-start gap-2">
              <Icons.Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-300">
                <p className="font-medium text-blue-400 mb-1">Event Hub Integration</p>
                <p>
                  Logs are ingested via Azure Event Hub. Ensure your Azure Web App has
                  diagnostic settings configured to send logs to the Event Hub.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={loading || !name.trim()}
            >
              {loading ? (
                <>
                  <Icons.Spinner className="w-4 h-4" />
                  Adding...
                </>
              ) : (
                <>
                  <Icons.Plus className="w-4 h-4" />
                  Add App
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
